// PurchaseReturn_Listing.jsx
// Connects to Node backend:
//   GET  /api/purchaseReturn/init   → dropdowns (supplier, store, project)
//   GET  /api/purchaseReturn/list   → grid data (search/filter)
// Row click  → navigate to PurchaseReturn_Entry with { purRetId, isViewMode: true }
// Add button → navigate to PurchaseReturn_Entry (new mode)

import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import {
  Plus,
  Search,
  Filter,
  Eye,
  X,
  Pencil,
  FileText,
  Building2,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "https://nissi-india.com/api/purchaseReturn";

/* ─── Helpers ───────────────────────────────────────────────────────────── */
// yyyy-MM-dd  →  dd/MM/yyyy  (for Node API)
const toApiDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// dd/MM/yyyy → yyyy-MM-dd (for input[type="date"])
const toInputDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-")) return dateStr;
  const [d, m, y] = dateStr.split("/");
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const PurchaseReturnListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ── session / localStorage ─────────────────────────────── */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const storedBranchId = parseInt(branchId || "8", 10);

  const initialBranchType =
    branch === "HO" || branch === "HEAD-OFFICE" ? "HO" : "Branch";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── filter state ───────────────────────────────────────── */
  const [branchType, setBranchType] = useState(initialBranchType);
  const [supplierList, setSupplierList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [projNames, setProjNames] = useState([]);
  const [projCodes, setProjCodes] = useState([]);

  const [supplier, setSupplier] = useState("");
  const [store, setStore] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [purRetNo, setPurRetNo] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ── ui state ───────────────────────────────────────────── */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  const navigate = useNavigate();

  /* ─────────────────────────────────────────────────────────
     1. INIT — load all dropdowns
  ───────────────────────────────────────────────────────── */
  useEffect(() => {
    setInitLoading(true);

    axios
      .get(`${BASE_URL}/init`, {
        params: { branchId: storedBranchId, branchType },
        headers: {
          ...commonHeaders,
          "x-branchtype": branchType,
        },
      })
      .then((res) => {
        if (!res.data.success) throw new Error(res.data.message);

        setSupplierList(res.data.suppliers || []);
        setStoreList(res.data.stores || []);
        setProjNames(res.data.projectNames || []);
        setProjCodes(res.data.projectCodes || []);

        if (res.data.defaultFromDate) {
          setFromDate(toInputDate(res.data.defaultFromDate));
        }
        if (res.data.defaultToDate) {
          setToDate(toInputDate(res.data.defaultToDate));
        }
      })
      .catch((err) => {
        console.error("Init error:", err);
        toast.error("Failed to load dropdowns");
      })
      .finally(() => setInitLoading(false));
  }, [branchType, storedBranchId, commonHeaders]);

  /* ─────────────────────────────────────────────────────────
     2. BRANCH TYPE CHANGE
  ───────────────────────────────────────────────────────── */
  const handleBranchTypeChange = (val) => {
    setBranchType(val);
    setSupplier("");
    setStore("");
    setProjectName("");
    setProjectCode("");
    setPurRetNo("");
    setData([]);
  };

  /* ─────────────────────────────────────────────────────────
     3. PROJECT CODE ↔ NAME SYNC
  ───────────────────────────────────────────────────────── */
  const handleProjectCodeChange = (val) => {
    setProjectCode(val);
    const match = projNames.find((p) => p.value === val);
    setProjectName(match ? match.value : "");
  };

  const handleProjectNameChange = (val) => {
    setProjectName(val);
    const match = projCodes.find((p) => p.value === val);
    setProjectCode(match ? match.value : "");
  };

  /* ─────────────────────────────────────────────────────────
     4. SEARCH
  ───────────────────────────────────────────────────────── */
  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.get(`${BASE_URL}/list`, {
        params: {
          supplierid: supplier || "",
          branchId: storedBranchId,
          purRetNo: purRetNo || "",
          fromDate: toApiDate(fromDate),
          toDate: toApiDate(toDate),
          storeId: store || "",
          projectId: projectName || "",
          purType: branchType === "HO" ? "H" : "B",
          page: 1,
          pageSize: 200,
        },
        headers: {
          ...commonHeaders,
          "x-branchtype": branchType,
        },
      });

      if (!res.data.success) throw new Error(res.data.message);

      const rows = res.data.rows || [];
      setData(rows);

      if (rows.length === 0) {
        toast.info("No records found for selected filters");
      }
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     5. VIEW MODE
  ───────────────────────────────────────────────────────── */
  const handleViewRow = (row) => {
    navigate("/inventory/PurchaseReturn_Entry", {
      state: {
        purRetId: row.fld_purchasereturnid,
        isViewMode: true,
      },
    });
  };

  /* ─────────────────────────────────────────────────────────
     6. EDIT MODE
  ───────────────────────────────────────────────────────── */
  const handleEditRow = (row) => {
    navigate("/inventory/PurchaseReturn_Entry", {
      state: {
        purRetId: row.fld_purchasereturnid,
        isViewMode: false,
      },
    });
  };

  /* ─────────────────────────────────────────────────────────
     7. CLEAR FILTERS
  ───────────────────────────────────────────────────────── */
  const clearFilters = () => {
    setSupplier("");
    setStore("");
    setProjectName("");
    setProjectCode("");
    setPurRetNo("");
    setFromDate("");
    setToDate("");
    setData([]);
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Purchase Return Listing</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Purchase Return Records</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Search and manage purchase return documents
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.length} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
              >
                {showFilters ? <X size={16} /> : <Filter size={16} />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/inventory/PurchaseReturn_Entry")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>

          {/* Branch Type Toggle */}
          <div>
            <label className={labelClass}>Branch Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleBranchTypeChange("HO")}
                disabled={branch !== "HO"}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white shadow`
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${branch !== "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Building2 size={16} />
                H.O
              </button>

              <button
                type="button"
                onClick={() => handleBranchTypeChange("Branch")}
                disabled={branch === "HO"}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white shadow"
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${branch === "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              {initLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                  <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Loading filters…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    <div>
                      <label className={labelClass}>
                        From Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        To Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Supplier</label>
                      <select
                        value={supplier}
                        onChange={(e) => setSupplier(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select</option>
                        {supplierList.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Store</label>
                      <select
                        value={store}
                        onChange={(e) => setStore(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select</option>
                        {storeList.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Project Code</label>
                      <select
                        value={projectCode}
                        onChange={(e) => handleProjectCodeChange(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select</option>
                        {projCodes.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Project Name</label>
                      <select
                        value={projectName}
                        onChange={(e) => handleProjectNameChange(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select</option>
                        {projNames.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Purchase Return No</label>
                      <input
                        type="text"
                        value={purRetNo}
                        onChange={(e) => setPurRetNo(e.target.value)}
                        placeholder="Enter PR No"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={clearFilters}
                      className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                        isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Clear Filters
                    </button>

                    <button
                      onClick={handleSearch}
                      disabled={loading}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                    >
                      {loading ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search size={16} />
                      )}
                      {loading ? "Searching…" : "Search Records"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Purchase Return List</h3>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    {[
                      "S.No",
                      "PR No",
                      "Supplier",
                      "Date",
                      "LR No",
                      "Courier Service",
                      "Remarks",
                      "Action",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {data.map((row, i) => (
                    <tr
                      key={row.fld_purchasereturnid || i}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-center">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {row.fld_purchaseretno || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {row.fld_suppliername || "—"}
                      </td>
                      <td className="px-4 py-3">{row.fld_date || "—"}</td>
                      <td className="px-4 py-3">{row.fld_LRNo || "—"}</td>
                      <td className="px-4 py-3">
                        {row.fld_CourierService || "—"}
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate text-gray-500">
                        {row.fld_Remarks || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewRow(row)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
                          >
                            <Eye size={14} />
                            View
                          </button>

                          <button
                            onClick={() => handleEditRow(row)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg shadow transition"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Adjust your filters and search, or add a new Purchase Return.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseReturnListing;